import { useEffect, useState } from "react"
import axios from "axios"
import { isDevModeEnabled } from "lib/Cookie"
import * as DevModeService from "lib/DevMode/DevModeService"
import { SplitFactFindData } from "lib/SplitFactFind"

/**
 * Says what went wrong in words, with the api's own answer if it gave one. An axios error logs as
 * an empty object in the dev error overlay, which leaves out the part that says why.
 */
const describeError = (e: unknown): string => {
  const { message, response } = (e ?? {}) as {
    message?: string
    response?: { status?: number; data?: unknown }
  }
  if (!response) return message ?? String(e)
  return `HTTP ${response.status}: ${JSON.stringify(response.data ?? message)}`
}

/**
 * What Salesforce holds of the customer's split fact find answers, from `/split-fact-find/{friendlyId}`.
 * That reads nine Salesforce objects, so it is only requested when `enabled`: when the page is
 * going to use it. `splitFactFind` stays null until it arrives, and `error` is set instead if it
 * can't be read (the caller isn't signed in or doesn't own the application, or Salesforce failed).
 */
export const useSplitFactFind = (friendlyId: string | undefined, enabled: boolean) => {
  const [splitFactFind, setSplitFactFind] = useState<SplitFactFindData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!friendlyId || !enabled) return

    // A response that arrives after the page has moved on mustn't update it
    let isStale = false

    const request: Promise<SplitFactFindData> = isDevModeEnabled()
      ? DevModeService.getSplitFactFind()
      : axios
          .get<SplitFactFindData>(`${process.env.MY_MOJO_API}/split-fact-find/${friendlyId}`, {
            withCredentials: true,
          })
          .then(({ data }) => data)

    request
      .then((data) => {
        if (isStale) return
        setError(null)
        setSplitFactFind(data)
      })
      .catch((e) => {
        if (isStale) return
        // One string: the dev error overlay only shows the first argument
        console.error(`Error fetching the split fact find: ${describeError(e)}`)
        setError("Failed to fetch the split fact find")
      })

    return () => {
      isStale = true
    }
  }, [friendlyId, enabled])

  return { splitFactFind, error }
}

export default useSplitFactFind
